import {
  applyModificationsViaIntents,
  extractPresentationContentViaIntents,
  getPreslinkFilePathsViaIntents,
  type FactSetPresentationRef,
  type FactSetPreslinkFilePathShape,
} from "../../../services/factset/intentBridgeService";
import { parsePreslinkTagMainFields } from "../../../services/ai/preslinkWorkbook";
import type { ShapeContent, SlideContent } from "../../../types";
import type {
  PersistedPreslinkWorkbook,
  PersistedPreslinkWorkbooks,
} from "./preslinkInsertionService";
import type {
  SlideLibraryInsertionResult,
} from "./insertionService";
import { isPreslinkDmsoftTag } from "./preslinkPptxTags";

type Logger = (message: string) => void;

export interface PreslinkBindingWorkbook {
  localFileName: string;
  localPath: string;
}

interface PreslinkShapeTarget {
  slideId: string;
  slideIndex: number;
  shape: ShapeContent;
  workbook: PreslinkBindingWorkbook;
}

function escapeXmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function xmlAttributePattern(attribute: string): RegExp {
  return new RegExp(`(\\b${attribute}\\s*=\\s*)(["'])(.*?)\\2`, "i");
}

function replaceXmlAttribute(xml: string, attribute: string, value: string): string {
  const attributePattern = xmlAttributePattern(attribute);
  if (!attributePattern.test(xml)) {
    throw new Error(`PresLink tag is missing the ${attribute} attribute.`);
  }
  return xml.replace(
    attributePattern,
    (_match, prefix: string, quote: string) =>
      `${prefix}${quote}${escapeXmlAttribute(value)}${quote}`
  );
}

export function rewritePreslinkWorkbookTag(
  tag: string,
  workbook: PreslinkBindingWorkbook
): string {
  parsePreslinkTagMainFields({ DMSOFTTAG: tag });
  // FactSet keeps the trailing separator on Path, and Manage Links shows Path verbatim.
  const withPath = replaceXmlAttribute(tag, "Path", workbook.localPath.slice(
    0,
    workbook.localPath.length - workbook.localFileName.length
  ));
  const withFileName = replaceXmlAttribute(withPath, "FileName", workbook.localFileName);
  // Manage Links shows FileDesc, not FileName, as the workbook's file name.
  return xmlAttributePattern("FileDesc").test(withFileName)
    ? replaceXmlAttribute(withFileName, "FileDesc", workbook.localFileName)
    : withFileName;
}

export function readPreslinkWorkbookFileNameFromShape(shape: ShapeContent): string {
  return readPreslinkWorkbookTagFields(shape).fileName;
}

export function readPreslinkWorkbookTagFields(shape: ShapeContent): {
  fileName: string;
  path: string;
} {
  const tagEntry = findTagEntry(shape.tags, "DMSOFTTAG");
  if (!tagEntry) {
    throw new Error(`PresLink shape ${shape.id} has no DMSOFTTAG.`);
  }
  const fields = parsePreslinkTagMainFields({ DMSOFTTAG: tagEntry[1] });
  return {
    fileName: fields.fileName,
    path: fields.path,
  };
}

function basename(value: string): string {
  return value.trim().split(/[\\/]/).filter(Boolean).pop() ?? "";
}

function normalizePath(value: string): string {
  return value.replace(/[\\/]+/g, "/").replace(/\/$/, "").toLowerCase();
}

function findTagEntry(
  tags: Record<string, string> | undefined,
  name: string
): [key: string, value: string] | undefined {
  if (!tags) return undefined;
  const wanted = name.toLowerCase();
  const key = Object.keys(tags).find((candidate) => candidate.toLowerCase() === wanted);
  return key ? [key, tags[key]] : undefined;
}

function collectPreslinkShapes(shapes: ShapeContent[]): ShapeContent[] {
  return shapes.flatMap((shape) => [
    ...(isPreslinkDmsoftTag(findTagEntry(shape.tags, "DMSOFTTAG")?.[1] ?? "") ? [shape] : []),
    ...(shape.shapes ? collectPreslinkShapes(shape.shapes) : []),
  ]);
}

function joinedTagPath(fields: { fileName: string; path: string }): string {
  if (!fields.path.trim()) return normalizePath(fields.fileName);
  const separator = fields.path.includes("\\") ? "\\" : "/";
  return normalizePath(`${fields.path.replace(/[\\/]+$/, "")}${separator}${fields.fileName}`);
}

function pathContainsWorkbookId(path: string, workbookId: string): boolean {
  const id = workbookId.trim().toLowerCase();
  if (!id) return false;
  return normalizePath(path).split("/").includes(id);
}

function tagIdentifiesWorkbook(
  fields: { fileName: string; path: string },
  workbook: PersistedPreslinkWorkbook
): boolean {
  const tagFileName = basename(fields.fileName).toLowerCase();
  if (pathContainsWorkbookId(fields.path, workbook.workbookId)) return true;
  if (normalizePath(workbook.localPath) === joinedTagPath(fields)) return true;
  return (
    tagFileName.length > 0 &&
    (tagFileName === basename(workbook.originalFileName).toLowerCase() ||
      tagFileName === basename(workbook.localFileName).toLowerCase())
  );
}

function resolveWorkbookForShape(
  shape: ShapeContent,
  expectedWorkbookIds: string[],
  persistedWorkbooks: Map<string, PersistedPreslinkWorkbook>
): PersistedPreslinkWorkbook {
  const fields = readPreslinkWorkbookTagFields(shape);
  const candidates = expectedWorkbookIds
    .map((workbookId) => persistedWorkbooks.get(workbookId))
    .filter((workbook): workbook is PersistedPreslinkWorkbook => Boolean(workbook));
  const identified = candidates.filter((workbook) => tagIdentifiesWorkbook(fields, workbook));
  const resolvedTagPath = joinedTagPath(fields);
  const identifiedByIdentity = identified.filter(
    (workbook) =>
      pathContainsWorkbookId(fields.path, workbook.workbookId) ||
      normalizePath(workbook.localPath) === resolvedTagPath
  );
  if (identifiedByIdentity.length === 1) return identifiedByIdentity[0];
  if (identifiedByIdentity.length > 1) {
    throw new Error(`PresLink shape ${shape.id} has an ambiguous workbook path.`);
  }
  if (identified.length === 1) return identified[0];
  if (identified.length > 1) {
    throw new Error(`PresLink shape ${shape.id} has an ambiguous workbook filename.`);
  }
  if (candidates.length === 1) return candidates[0];
  throw new Error(
    `Could not map PresLink shape ${shape.id} workbook "${fields.fileName}" to the selected collection workbooks.`
  );
}

function findInsertedSlide(
  presentation: { slides: SlideContent[] },
  insertedSlideId: string
): SlideContent {
  const slide = presentation.slides.find((candidate) => candidate.id === insertedSlideId);
  if (!slide) {
    throw new Error(`Inserted PresLink slide ${insertedSlideId} was not returned by the host.`);
  }
  return slide;
}

function findPathShape(
  pathResult: Awaited<ReturnType<typeof getPreslinkFilePathsViaIntents>>,
  slideId: string,
  shapeId: string
): FactSetPreslinkFilePathShape | undefined {
  return pathResult.slides
    .find((slide) => slide.slideId === slideId)
    ?.shapes.find((shape) => shape.shapeId === shapeId);
}

async function verifyPreslinkShapePaths(input: {
  targets: PreslinkShapeTarget[];
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
}): Promise<void> {
  const pathResult = await getPreslinkFilePathsViaIntents({
    shapeRefs: input.targets.map((target) => ({
      slideId: target.slideId,
      id: target.shape.id,
    })),
    presentationRef: input.presentationRef,
    log: input.log,
  });

  for (const target of input.targets) {
    const shape = findPathShape(pathResult, target.slideId, target.shape.id);
    const expectedPath = normalizePath(target.workbook.localPath);
    if (!shape || !shape.files.some((file) => normalizePath(file.filePath) === expectedPath)) {
      throw new Error(
        `PresLink binding verification failed for shape ${target.shape.id}; expected ${target.workbook.localFileName}.`
      );
    }
  }
}

async function applyAndVerifyPreslinkBindingTargets(input: {
  targets: PreslinkShapeTarget[];
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
}): Promise<void> {
  const operationsBySlide = new Map<
    string,
    {
      id: string;
      index: number;
      action: "modify";
      shapes: Array<{ id: string; action: "modify"; tags: Record<string, string> }>;
    }
  >();

  for (const target of input.targets) {
    const existingTags = target.shape.tags;
    const tagEntry = findTagEntry(existingTags, "DMSOFTTAG");
    if (!tagEntry) {
      throw new Error(`PresLink shape ${target.shape.id} has no DMSOFTTAG.`);
    }
    const rewrittenTag = rewritePreslinkWorkbookTag(tagEntry[1], target.workbook);
    if (rewrittenTag === tagEntry[1]) continue;
    let operation = operationsBySlide.get(target.slideId);
    if (!operation) {
      operation = {
        id: target.slideId,
        index: target.slideIndex,
        action: "modify",
        shapes: [],
      };
      operationsBySlide.set(target.slideId, operation);
    }
    operation.shapes.push({
      id: target.shape.id,
      action: "modify",
      tags: {
        ...existingTags,
        [tagEntry[0]]: rewrittenTag,
      },
    });
  }

  const operations = Array.from(operationsBySlide.values()).filter(
    (operation) => operation.shapes.length > 0
  );
  if (operations.length > 0) {
    const requiredMutations = operations.reduce(
      (count, operation) => count + operation.shapes.length,
      0
    );
    const summary = await applyModificationsViaIntents(
      { slides: operations },
      input.log,
      { presentationRef: input.presentationRef }
    );
    if (summary.errors.count > 0 || summary.applied.shapes < requiredMutations) {
      throw new Error("The host could not apply all PresLink workbook path updates.");
    }
  }

  await verifyPreslinkShapePaths({
    targets: input.targets,
    presentationRef: input.presentationRef,
    log: input.log,
  });
}

export interface InsertedPreslinkSlideWorkbooks {
  insertedSlideId: string;
  /** The workbooks saved for this slide's insert; each shape must point at exactly one of them. */
  workbooks: PreslinkBindingWorkbook[];
}

/**
 * Check, without changing any tag, that every PresLink shape on the inserted
 * slides resolves only to the workbook saved for it.
 */
export async function verifyInsertedPreslinkBindings(input: {
  slides: InsertedPreslinkSlideWorkbooks[];
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
}): Promise<void> {
  if (input.slides.length === 0) return;
  const presentation = await extractPresentationContentViaIntents(input.log, {
    forceRefresh: true,
    presentationRef: input.presentationRef,
    filters: {
      slides: {
        ids: input.slides.map((slide) => slide.insertedSlideId),
        shapes: {
          include: true,
          tags: true,
        },
      },
    },
  });
  const targets: PreslinkShapeTarget[] = [];
  for (const inserted of input.slides) {
    const slide = findInsertedSlide(presentation, inserted.insertedSlideId);
    const shapes = collectPreslinkShapes(slide.shapes);
    if (shapes.length === 0) {
      throw new Error(`Inserted PresLink slide ${inserted.insertedSlideId} contains no PresLink shapes.`);
    }
    for (const shape of shapes) {
      const fileName = readPreslinkWorkbookFileNameFromShape(shape).toLowerCase();
      const matching = new Map(
        inserted.workbooks
          .filter((workbook) => workbook.localFileName.toLowerCase() === fileName)
          .map((workbook) => [normalizePath(workbook.localPath), workbook] as const)
      );
      if (matching.size !== 1) {
        throw new Error(
          `PresLink shape ${shape.id} does not point at exactly one workbook saved for this insert.`
        );
      }
      targets.push({
        slideId: slide.id,
        slideIndex: slide.index,
        shape,
        workbook: Array.from(matching.values())[0],
      });
    }
  }

  const pathResult = await getPreslinkFilePathsViaIntents({
    shapeRefs: targets.map((target) => ({ slideId: target.slideId, id: target.shape.id })),
    presentationRef: input.presentationRef,
    log: input.log,
  });
  for (const target of targets) {
    const files = findPathShape(pathResult, target.slideId, target.shape.id)?.files ?? [];
    const expectedPath = normalizePath(target.workbook.localPath);
    if (files.length === 0 || !files.every((file) => normalizePath(file.filePath) === expectedPath)) {
      throw new Error(
        `PresLink binding verification failed for shape ${target.shape.id}; expected ${target.workbook.localFileName}.`
      );
    }
  }
}

/** The source slide's PresLink shapes, when it still has exactly `count` of them. */
function findSourcePreslinkShapes(
  presentation: { slides: SlideContent[] },
  sourceSlideId: string | undefined,
  count: number
): ShapeContent[] | undefined {
  const source = sourceSlideId
    ? presentation.slides.find((candidate) => candidate.id === sourceSlideId)
    : undefined;
  const shapes = source ? collectPreslinkShapes(source.shapes) : [];
  return shapes.length === count ? shapes : undefined;
}

/**
 * Rebind inserted PresLink shapes using a caller-provided workbook resolver.
 * This is shared by collection insertion and refresh replacement, where the
 * source of the exact local workbook path differs.
 */
export async function remapPreslinkBindingsForInsertedSlides(input: {
  insertedSlideIds: string[];
  /**
   * The slide, still in the deck, that each inserted slide is a processed copy
   * of. A processed copy keeps its source's PresLink shapes in the same order,
   * so `resolveWorkbook` hears which source shape each copied shape came from.
   */
  sourceSlideIdByInsertedSlideId?: ReadonlyMap<string, string>;
  resolveWorkbook: (
    slideId: string,
    shape: ShapeContent,
    sourceShape?: ShapeContent
  ) => PreslinkBindingWorkbook;
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
}): Promise<void> {
  const insertedSlideIds = Array.from(
    new Set(input.insertedSlideIds.map((slideId) => slideId.trim()).filter(Boolean))
  );
  if (insertedSlideIds.length === 0) return;
  const sourceSlideIds = insertedSlideIds.flatMap((slideId) => {
    const sourceSlideId = input.sourceSlideIdByInsertedSlideId?.get(slideId);
    return sourceSlideId ? [sourceSlideId] : [];
  });

  const presentation = await extractPresentationContentViaIntents(input.log, {
    forceRefresh: true,
    presentationRef: input.presentationRef,
    filters: {
      slides: {
        ids: [...insertedSlideIds, ...sourceSlideIds],
        shapes: {
          include: true,
          tags: true,
        },
      },
    },
  });
  const targets: PreslinkShapeTarget[] = [];
  for (const insertedSlideId of insertedSlideIds) {
    const slide = findInsertedSlide(presentation, insertedSlideId);
    const shapes = collectPreslinkShapes(slide.shapes);
    if (shapes.length === 0) {
      throw new Error(`Inserted PresLink slide ${insertedSlideId} contains no PresLink shapes.`);
    }
    const sourceShapes = findSourcePreslinkShapes(
      presentation,
      input.sourceSlideIdByInsertedSlideId?.get(insertedSlideId),
      shapes.length
    );
    shapes.forEach((shape, position) => {
      targets.push({
        slideId: slide.id,
        slideIndex: slide.index,
        shape,
        workbook: input.resolveWorkbook(slide.id, shape, sourceShapes?.[position]),
      });
    });
  }

  await applyAndVerifyPreslinkBindingTargets({
    targets,
    presentationRef: input.presentationRef,
    log: input.log,
  });
}

export async function remapInsertedPreslinkBindings(input: {
  insertionResult: SlideLibraryInsertionResult;
  persisted: PersistedPreslinkWorkbooks;
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
}): Promise<void> {
  const successes = input.insertionResult.successes;
  if (successes.length === 0) return;
  const insertedSlideIds = successes.flatMap((success) =>
    success.insertedSlides.map((slide) => slide.insertedSlideId)
  );
  const successByInsertedSlideId = new Map(
    successes.flatMap((success) =>
      success.insertedSlides.map((insertedSlide) => [insertedSlide.insertedSlideId, success] as const)
    )
  );
  await remapPreslinkBindingsForInsertedSlides({
    insertedSlideIds,
    resolveWorkbook: (slideId, shape) => {
      const success = successByInsertedSlideId.get(slideId);
      if (!success) {
        throw new Error(`Inserted PresLink slide ${slideId} has no insertion mapping.`);
      }
      const expectedWorkbookIds = input.persisted.workbookIdsBySlideId.get(success.item.id);
      if (!expectedWorkbookIds || expectedWorkbookIds.length === 0) {
        throw new Error(`No persisted workbook mapping was found for slide ${success.item.id}.`);
      }
      return resolveWorkbookForShape(shape, expectedWorkbookIds, input.persisted.workbooks);
    },
    presentationRef: input.presentationRef,
    log: input.log,
  });
}
